import React from "react";
import RebalanceInventoryImage from "../resources/RebalanceInventory.png";
import RecycleInventoryImage from "../resources/RecycleInventory.png";
import TransferInventoryImage from "../resources/TransferInventory.png";
import {Box, FlashbarProps, SpaceBetween} from "@amzn/awsui-components-react-v3";
import {SearchType} from "../../interfaces/inventoryViewCommon";
import {Link} from 'react-router-dom';
import {AppConfig} from "../../pages/config/AppConfig";

export enum CELL_VALUES {
    ALPHA_CELL =  "prod.cell-1",
    BETA_CELL = "prod.cell-2",
    GAMMA_CELL = "prod.cell-3",
    PROD_CELL = "prod.cell-4"
}

export enum STAGE_NAMES {
    ALPHA = "alpha",
    BETA = "beta",
    GAMMA = "prod.cell-3",
    PROD = "prod.cell-4"
}

export const INVENTORY_MANAGEMENT_EMAIL = "aws-scm-inventory-management@amazon.com";

export const EMPTY = "";

export const swiftConfig = [
    {
        "cell": CELL_VALUES.ALPHA_CELL,
        "SWIFT_HIDDEN": false,
        "STAGE": STAGE_NAMES.ALPHA
    },
    {
        "cell": CELL_VALUES.BETA_CELL,
        "SWIFT_HIDDEN": false,
        "STAGE": STAGE_NAMES.BETA
    },
    {
        "cell": CELL_VALUES.GAMMA_CELL,
        "SWIFT_HIDDEN": true,
        "STAGE": STAGE_NAMES.GAMMA
    },
    {
        "cell": CELL_VALUES.PROD_CELL,
        "SWIFT_HIDDEN": true,
        "STAGE": STAGE_NAMES.PROD
    },
]

export enum BULK_UPLOAD_FLASHBAR_STATE {
    SUCCESS = "BULK_UPLOAD_FLASHBAR_SUCCESS",
    ERROR = "BULK_UPLOAD_FLASHBAR_ERROR"
}

export enum NEW_REQUEST_FLASHBAR {
    TITLE = "New request created",
    DESCRIPTION = "Your new request has been successfully created with SWIFT ID: ",
    ADDITIONAL_DESCRIPTION = "Please check after sometime.",
    REQUEST_CREATED = "REQUEST_CREATED"
}

export enum CANCEL_REQUEST_FLASHBAR {
    TITLE = "Request cancelled successfully",
    DESCRIPTION = "Your request has been successfully cancelled for SWIFT ID: ",
    ADDITIONAL_DESCRIPTION = "Please check after sometime.",
    REQUEST_CANCELLED = "REQUEST_CANCELLED"
}

export const flashbarDetails = ({uploadedFileName = "", swiftReadableId = ""}): FlashbarProps.MessageDefinition[] => {
    return [
        {
            header: "File submitted",
            content: "Your new request has been successfully created.",
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            id: BULK_UPLOAD_FLASHBAR_STATE.SUCCESS,
        },
        {
            header: "Failed to submit",
            content: `An error has been detected in ${uploadedFileName}`,
            type: "error",
            dismissible: true,
            dismissLabel: "Dismiss message",
            id: BULK_UPLOAD_FLASHBAR_STATE.ERROR,
        },
        {
            header: "",
            content: <SpaceBetween direction="vertical" size="xxs">
                <strong>{NEW_REQUEST_FLASHBAR.TITLE}</strong>
                <Box>
                    <span>{NEW_REQUEST_FLASHBAR.DESCRIPTION + `${swiftReadableId}. `+ NEW_REQUEST_FLASHBAR.ADDITIONAL_DESCRIPTION}</span>
                </Box>
            </SpaceBetween>,
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            id: NEW_REQUEST_FLASHBAR.REQUEST_CREATED
        },
        {
            header: "",
            content: <SpaceBetween direction="vertical" size="xxs">
                        <strong>{CANCEL_REQUEST_FLASHBAR.TITLE}</strong>
                        <Box>
                            <span>{CANCEL_REQUEST_FLASHBAR.DESCRIPTION + `${swiftReadableId}. ` + CANCEL_REQUEST_FLASHBAR.ADDITIONAL_DESCRIPTION}</span>
                        </Box>
                    </SpaceBetween>,
            type: "success",
            dismissible: true,
            dismissLabel: "Dismiss message",
            id: CANCEL_REQUEST_FLASHBAR.REQUEST_CANCELLED
        },
    ];
}

export enum RESPONSE_CODE {
    SUCCESS_RESPONSE = 200,
}

export enum LocationNameType {
    SOURCE = "source",
    DESTINATION = "destination",
}

export const singleCreateRequestValidLocations = ["RAD001", "RDE001"];

export const STATE_CONSTANTS = {
    SELECT_PURPOSE_STATE: "Rebalance Inventory",
    SELECT_PROGRAM_STATE: "Spare Components",
    SELECT_CREATION_METHOD_STATE: "Single create",
};

export const DEFAULT_PREF_CHECKBOX = {
    DESCRIPTION: "Skip this step automatically next time. You can still manually return to this page to make changes"
}

export enum CREATION_METHOD_TITLE {
    SINGLE_CREATE = "Single create",
    BULK_CREATE = "Bulk create",
}

export enum SELECT_PURPOSE_TITLE {
    REBALANCE_INVENTORY = "Rebalance Inventory",
    RECYCLE_INVENTORY = "Recycle Inventory",
    TRANSFER_INVENTORY = "Transfer Inventory",
}

export enum SELECT_PURPOSE_DESCRIPTION {
    REBALANCE_INVENTORY_DESC = "Move inventory for deployment and/or break/fix purpose",
    RECYCLE_INVENTORY_DESC = "Move inventory for end of life refurbishment and liquidation purposes",
    TRANSFER_INVENTORY_DESC = "Move inventory to facilitate capacity availability",
}

export enum SELECT_PROGRAM_TITLE {
    SPARE_COMPONENTS = "Spare Components",
    LOOSE_COMPONENTS = "Loose Components",
    PRODUCTION_COMPONENTS = "Production Components",
    RACKS = "Racks",
}

export const PROGRAM_TYPE_MAPPING = {
    [SELECT_PROGRAM_TITLE.PRODUCTION_COMPONENTS] : "production_component_allocation" ,
    [SELECT_PROGRAM_TITLE.SPARE_COMPONENTS] : "spares_allocation"
}

export const CREATION_METHOD_TYPE_MAPPING = {
    [CREATION_METHOD_TITLE.SINGLE_CREATE] : "single_create" ,
    [CREATION_METHOD_TITLE.BULK_CREATE] : "bulk_create"
}

export const PURPOSE_TYPE_MAPPING = {
    [SELECT_PURPOSE_TITLE.REBALANCE_INVENTORY] : "rebalance" ,
    [SELECT_PURPOSE_TITLE.RECYCLE_INVENTORY] : "recycle",
    [SELECT_PURPOSE_TITLE.TRANSFER_INVENTORY] : "transfer"
}
export const getProgramTypeValue = (programTitle: string): string => {
    return PROGRAM_TYPE_MAPPING[programTitle];
};

export const getCreationMethodTypeValue = (creationMethodTitle: string): string => {
    return CREATION_METHOD_TYPE_MAPPING[creationMethodTitle];
};

export const getPurposeTypeValue = (purposeTitle: string): string => {
    return PURPOSE_TYPE_MAPPING[purposeTitle];
};

interface CreationMethod {
    label: string;
    description: string;
    value: string;
    purpose: string[];
    program: string[];
}

export enum CREATION_METHOD_DESCRIPTION {
    SINGLE_CREATE_DESC = "We will guide you through the creation flow step by step, validating your request throughout the process.",
    BULK_CREATE_DESC = "We'll provide an Excel template for your request types and validate them after you upload your list.",
}

export enum CREATE_REQUEST_TITLE {
    SELECT_PURPOSE = "Select purpose",
    SELECT_PROGRAM = "Select program",
    SELECT_CREATION_METHOD = "Select creation method ",
    CREATE_NEW_REQUEST = "Create new request",
}

export const creationMethod: CreationMethod[] = [
    {
        label: CREATION_METHOD_TITLE.SINGLE_CREATE,
        description: CREATION_METHOD_DESCRIPTION.SINGLE_CREATE_DESC,
        value: "Single create",
        purpose: ["Rebalance Inventory"],
        program: ["Spare Components", "Production Components"]
    },
    {
        label: CREATION_METHOD_TITLE.BULK_CREATE,
        description: CREATION_METHOD_DESCRIPTION.BULK_CREATE_DESC,
        value: "Bulk create",
        purpose: ["Rebalance Inventory"],
        program: ["Spare Components", "Production Components"]
    }
];

interface Program {
    label: string;
    description: string;
    value: string;
    purpose: string[];
    hidden?: boolean;
}

export const program: Program[] = [
    {
        label: SELECT_PROGRAM_TITLE.SPARE_COMPONENTS,
        description: "",
        value: "Spare Components",
        purpose: ["Rebalance Inventory"]
    },
    {
        label: SELECT_PROGRAM_TITLE.LOOSE_COMPONENTS,
        description: "",
        value: "Loose Components",
        purpose: ["Recycle Inventory"]
    },
    {
        label: SELECT_PROGRAM_TITLE.PRODUCTION_COMPONENTS,
        description: "",
        value: "Production Components",
        purpose: ["Rebalance Inventory"],
        hidden: AppConfig.logConfigStageKey() === CELL_VALUES.PROD_CELL
    },
    {
        label: SELECT_PROGRAM_TITLE.RACKS,
        description: "",
        value: "Racks",
        purpose: ["Transfer Inventory"]
    },
];

export const purpose = [
    {
        label: SELECT_PURPOSE_TITLE.REBALANCE_INVENTORY,
        description: SELECT_PURPOSE_DESCRIPTION.REBALANCE_INVENTORY_DESC,
        image: <img src={RebalanceInventoryImage} alt="placeholder" />,
        value: "Rebalance Inventory",
        hidden: false
    },
    {
        label: SELECT_PURPOSE_TITLE.RECYCLE_INVENTORY,
        description: SELECT_PURPOSE_DESCRIPTION.RECYCLE_INVENTORY_DESC,
        image: <img src={RecycleInventoryImage} alt="placeholder" />,
        value: "Recycle Inventory",
        hidden: true
    },
    {
        label: SELECT_PURPOSE_TITLE.TRANSFER_INVENTORY,
        description: SELECT_PURPOSE_DESCRIPTION.TRANSFER_INVENTORY_DESC,
        image: <img src={TransferInventoryImage} alt="placeholder" />,
        value: "Transfer Inventory",
        hidden: true
    },
];

export const projectFieldsForSource = (
    selectedIPN,
    selectedSource,
    selectedProjectName
) => [
    {
        name: "IPN",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [{value: selectedIPN}],
    },
    {
        name: "LOCATION_NAME",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [selectedSource],
    },
    {
        name: "PROJECT_TAG",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [selectedProjectName],
    },
];

export const projectFieldsForDestination = (
    selectedIPN,
    selectedDestination,
    selectedProjectName
) => [
    {
        name: "IPN",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [{value: selectedIPN}],
    },
    {
        name: "LOCATION_NAME",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [selectedDestination],
    },
    {
        name: "PROJECT_TAG",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [selectedProjectName],
    },
];

export const projectFieldsForSourceProduction = (
    selectedIPN,
    selectedSource
) => [
    {
        name: "IPN",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [{value: selectedIPN}],
    },
    {
        name: "LOCATION_NAME",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [selectedSource],
    }
];

export const projectFieldsForDestinationProduction = (
    selectedIPN,
    selectedDestination,
) => [
    {
        name: "IPN",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [{value: selectedIPN}],
    },
    {
        name: "LOCATION_NAME",
        searchType: SearchType.EXACT_MATCH,
        selectedData: [selectedDestination],
    }
];

export const inventoryLevelsStatus = (inTransitReq, onHand, allocated, available) => [
    {
        label: "In transit",
        value: inTransitReq,
    },
    {
        label: "On hand",
        value: onHand,
    },
    {
        label: "Allocated",
        value: allocated,
    },
    {
        label: "Available",
        value: available,
        customProps: {
            greenColor: true,
        },
    },
];

export enum TOWNSEND_REDIRECT_MAPPING {
    ALPHA = "https://scm-portal-alpha-pdx.aka.amazon.com/townsend/part/detail/",
    BETA = "https://aws-beta.argo.ocean-wave.aws.a2z.com/townsend/part/detail/",
    PROD = "https://aws.argo.ocean-wave.aws.a2z.com/townsend/part/detail/",
    DEV = "https://aws-beta.argo.ocean-wave.aws.a2z.com/townsend/part/detail/"
}

export enum SHIPMENT_NUMBER_REDIRECT_MAPPING {
    ALPHA = "https://scm-portal-alpha-pdx.aka.amazon.com/shipments/details/",
    BETA = "https://aws-beta.argo.ocean-wave.aws.a2z.com/shipments/details/",
    PROD = "https://aws.argo.ocean-wave.aws.a2z.com/shipments/details/",
    DEV = "https://aws-beta.argo.ocean-wave.aws.a2z.com/shipments/details/"
}

export const redirectRequestIdToTrackingPage = (requestId) => {
    return (
        <Link to={`/tracking-request?request_id=${requestId}`}>{requestId}</Link>
    )
}

export const redirectSwiftIdToTrackingPage = (swiftId, requestId) => {
    return (
        <Link to={`/tracking-request?request_id=${requestId}`}>{swiftId}</Link>
    )
}

export const SAVE = "save"
export const UNSAVE = "unsave"
export const YES = "Yes"
export const NO = "No"

export const defaultPreferencesOptions = [
        {
            value: YES,
            label: YES
        },
        {
            value: NO,
            label: NO
        }
]